/**
 * Repo — the platform-agnostic local store contract.
 *
 * Two responsibilities: a **cache** (read model, mirrored from /api/sync/pull)
 * and an **outbox** (write model, queued offline mutations). The sync engine
 * and all conflict/role logic sit ABOVE this; only the implementation differs
 * per platform (Dexie/IndexedDB on web, SQLite on Capacitor native). A shared
 * conformance suite asserts every adapter behaves identically.
 *
 * Local-first reads: getAnswerDetail layers pending outbox mutations OVER the
 * server cache, so an offline edit shows immediately, survives reload, and is
 * never clobbered by a pull.
 */
import type {
  Inspection,
  NegativeObservation,
  Rectification,
  Rank,
  QuestionAnswer,
  DeficiencyFindingListItem,
} from "@/types/api";
import type {
  OutboxMutation,
  OutboxAttachment,
  OutboxState,
  PullDelta,
  AnswerDetail,
} from "./types";

export interface Repo {
  /* ── cache reads ─────────────────────────────────────────────── */
  getInspection(id: number): Promise<Inspection | undefined>;
  listInspections(): Promise<Inspection[]>;
  /** All envelope rows for an inspection (drives the status summary). */
  listAnswers(inspectionId: number): Promise<QuestionAnswer[]>;
  /** One question's detail = server cache + optimistic outbox overlay. */
  getAnswerDetail(inspectionId: number, questionId: number): Promise<AnswerDetail>;
  listNegativeObservations(inspectionId: number): Promise<NegativeObservation[]>;
  /** Every cached observation (across all inspections) — for the global list. */
  listAllNegativeObservations(): Promise<NegativeObservation[]>;
  /** Queued (offline-created, not-yet-synced) NOMs for an inspection, synthesized
   *  from the outbox as optimistic NegativeObservation rows (negative ids) so the
   *  observations view can show them before they sync. */
  listQueuedNegativeObservations(inspectionId: number): Promise<NegativeObservation[]>;
  /** Same, but across ALL inspections — for the global Observations list. */
  listAllQueuedNegativeObservations(): Promise<NegativeObservation[]>;
  /** Offline-created deficiency findings (temp negative ids), synthesized from
   *  the per-inspection `findings:*` caches + stamped with vessel/inspection
   *  meta — so the global Observations list shows them before they sync. */
  listAllQueuedFindings(): Promise<DeficiencyFindingListItem[]>;
  /** Drop an offline-created finding (temp negative id) from the local
   *  `findings:*` / `deficiency-finding:*` caches once it has synced — so the
   *  Observations list doesn't show the temp row alongside the pulled real one. */
  evictQueuedFinding(tempId: number): Promise<void>;
  listRectifications(): Promise<Rectification[]>;
  getRanks(): Promise<Rank[]>;

  /* ── cache writes (from pull) ────────────────────────────────── */
  applyDelta(delta: PullDelta): Promise<void>;
  /** Drop an inspection + its child rows from the cache (eviction). */
  evictInspection(inspectionId: number): Promise<void>;
  /** Rewrite every queued mutation/attachment that references a temporary
   *  (offline-created) inspection id to the real server id, once the offline
   *  create has synced. Updates the outbox row's inspectionId, its payload, and
   *  its coalesceKey, plus any pending attachment's inspectionId. */
  remapInspectionId(tempId: number, serverId: number): Promise<void>;

  /** Remap an offline-created negative observation's temp (negative) id to its
   *  real server id once the NOM has synced. Updates dependent outbox rows
   *  (rootCause / rectification) — their negativeObservationId, payload, and
   *  coalesceKey — so they no longer reference the dead temp id (which the
   *  server rejects as "must be > 0"). */
  remapNegativeObservationId(tempId: number, serverId: number): Promise<void>;

  /** Remap an offline-created deficiency finding's temp (negative) id to its real
   *  server id once the finding has synced (replayFindings). Rewrites dependent
   *  outbox rows (rectification / rootCause referencing it in their payload +
   *  coalesceKey) and any queued attachment's ownerId, so those upload against the
   *  real finding instead of the dead temp id. */
  remapFindingId(tempId: number, serverId: number): Promise<void>;

  /* ── outbox ──────────────────────────────────────────────────── */
  /** Enqueue a mutation, coalescing onto any existing pending row with the
   *  same coalesceKey (latest payload + clientUpdatedAt win). */
  enqueue(m: Omit<OutboxMutation, "id" | "state" | "createdAt" | "seq"> & { seq?: number }): Promise<void>;
  listOutbox(states?: OutboxState[]): Promise<OutboxMutation[]>;
  markOutbox(ids: number[], state: OutboxState, error?: string | null): Promise<void>;
  deleteOutbox(ids: number[]): Promise<void>;
  /** True if an unsynced mutation exists for this logical row (overlay guard). */
  hasPending(coalesceKey: string): Promise<boolean>;

  /* ── attachment outbox ───────────────────────────────────────── */
  enqueueAttachment(a: Omit<OutboxAttachment, "id" | "state" | "createdAt">): Promise<void>;
  listAttachments(states?: OutboxState[]): Promise<OutboxAttachment[]>;
  markAttachment(id: number, state: OutboxState, error?: string | null): Promise<void>;
  deleteAttachment(id: number): Promise<void>;

  /* ── response cache (read-only enriched DTOs: inspection detail,
   *     questions list, summary digest — warmed online, served offline) ── */
  kvGet<T>(key: string): Promise<T | undefined>;
  kvPut(key: string, value: unknown): Promise<void>;
  /** Write many kv entries in ONE transaction. Far fewer disk syncs than N
   *  separate kvPut() calls — used by the warm-up, which writes several caches
   *  per inspection. */
  kvPutMany(entries: Array<{ key: string; value: unknown }>): Promise<void>;
  /** Drop every cached response whose key starts with `prefix`. Used after an
   *  online create/edit to evict the now-stale list/stats snapshots (e.g. all
   *  `inspections:list:*`) so the next offline read re-derives from the freshly
   *  updated mirror instead of serving a pre-mutation page. */
  kvDeleteByPrefix(prefix: string): Promise<void>;

  /* ── binary asset cache (attachment bytes, so images/audio show offline
   *     instead of dead S3 URLs) ── */
  putAsset(attachmentId: number, blob: Blob): Promise<void>;
  getAsset(attachmentId: number): Promise<Blob | undefined>;
  /** Atomically remove ONE attachment from a cached per-owner list (the kv
   *  Attachment[] at `kvKey`) AND its cached bytes (assets) in a single
   *  transaction — so an offline delete can never leave an orphaned blob or a
   *  half-updated list. Missing key/id is a harmless no-op. */
  deleteCachedAttachment(kvKey: string, attachmentId: number): Promise<void>;

  /* ── meta ────────────────────────────────────────────────────── */
  getCursor(): Promise<string | null>;
  setCursor(iso: string): Promise<void>;
  nextSeq(): Promise<number>;
  /** Count of pending+failed outbox mutations + attachments (for the UI badge). */
  pendingCount(): Promise<number>;
  /** Wipe everything (logout / account switch). */
  clear(): Promise<void>;
}

/** Natural coalesce keys — one place so the engine and adapters agree. */
export const coalesceKey = {
  envelope: (inspectionId: number, questionId: number) => `envelope:${inspectionId}:${questionId}`,
  aspect: (inspectionId: number, questionId: number, aspect: string) => `aspect:${inspectionId}:${questionId}:${aspect}`,
  humanRank: (inspectionId: number, questionId: number, rankId: number) => `humanRank:${inspectionId}:${questionId}:${rankId}`,
  photoComparison: (inspectionId: number, questionId: number) => `photoComparison:${inspectionId}:${questionId}`,
  // NOMs are insert-only → unique per clientId so they never coalesce together.
  nom: (clientId: string) => `nom:${clientId}`,
  // One rectification per owner (NOM or finding) → coalesce on the owner.
  rectification: (ownerKind: string, ownerId: number) => `rectification:${ownerKind}:${ownerId}`,
  // Root causes are insert-only → unique per clientId so each stays distinct.
  rootCause: (clientId: string) => `rootCause:${clientId}`,
  // Delete coalesces on the target row (deleting the same row twice is one op).
  rootCauseDelete: (rootCauseId: number) => `rootCause:delete:${rootCauseId}`,
  // Update coalesces on the target row (repeated edits collapse to the latest).
  rootCauseUpdate: (rootCauseId: number) => `rootCause:update:${rootCauseId}`,
  // One submit per inspection → repeated taps collapse to a single replay.
  inspectionSubmit: (inspectionId: number) => `inspectionSubmit:${inspectionId}`,
};
